import { format } from 'date-fns';
import type { EducationItem } from '../hooks/education-completion-hooks';
import type { UserJourneyMap } from '../hooks/enablement-journey-map-hooks';
import { cyberAcademyTrainingUrl } from './cyberAcademy';
import type { QuarterBounds } from './fiscalQuarters';

export type EnablementStatus = 'not_started' | 'in_progress' | 'completed';
export type EnablementSource = 'journey' | 'mindtickle';

export interface EnablementRow {
  id: string;
  source: EnablementSource;
  name: string;
  /** Second line under the name: program label and cadence, when there are any. */
  detail: string;
  required: boolean;
  status: EnablementStatus;
  percent: number;
  /** "4 of 6 steps"; empty when the count isn't known. */
  progress: string;
  /** Total estimated time in minutes, when the content has an estimate. */
  minutesTotal: number | null;
  /** Estimated time still to go; 0 once completed, null when there is no estimate. */
  minutesLeft: number | null;
  /** YYYY-MM-DD the content is due or stops being available, if known. */
  deadline: string | null;
  /** `due`: an assignment or cadence deadline. `expires`: the content stops being available. */
  deadlineKind: 'due' | 'expires' | null;
  completedOn: string | null;
  /** YYYY-MM-DD the item was assigned to this person, when the source says. */
  assignedOn?: string | null;
  /** YYYY-MM-DD a completed item's certificate lapses. */
  expiresOn?: string | null;
  /** Where the due date came from: the learner's own date, or the module's due-date rule. */
  dueSource?: 'learner' | 'rule' | null;
  /** Cyber Academy for journey maps, Mindtickle for series; always opens in a new tab. */
  href: string | null;
  external: boolean;
}

export interface JourneyCatalogEntry {
  id?: number;
  estimated_duration_minutes?: number | null;
  available_to?: string | null;
}

export interface BuildOptions {
  now?: Date;
  /** The journey-map catalog, for the estimated duration and availability end of each map. */
  catalog?: JourneyCatalogEntry[];
  /** Fiscal quarter bounds, used to turn a Mindtickle cadence window into a deadline. */
  bounds?: QuarterBounds | null;
  /** Due dates an admin entered by hand, keyed `journey_map:<id>` / `education_series:<id>` (YYYY-MM-DD). */
  adminDueDates?: Record<string, string>;
}

const MINDTICKLE_URL = 'https://cisco.mindtickle.com/new/ui/learner/training/programs/';
const WINDOWS: Record<string, string> = {
  current_quarter: 'This quarter',
  current_fiscal_year: 'This fiscal year',
  always_on: 'Always on',
};
const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));
const day = (v: string | null | undefined): string | null => (v ? String(v).slice(0, 10) : null);
const positive = (n: unknown): number | null => (Number(n) > 0 ? Number(n) : null);

export function journeyRows(maps: UserJourneyMap[], opts: BuildOptions = {}): EnablementRow[] {
  const catalog = new Map((opts.catalog || []).filter((c) => c?.id != null).map((c) => [Number(c.id), c]));
  return maps.map((m) => {
    const cat = catalog.get(m.mapId);
    const percent = m.done ? 100 : clamp(m.percent ?? (m.totalItems ? (m.completedItems / m.totalItems) * 100 : 0));
    const fromItems = (m.items || []).reduce((sum, it) => sum + (Number(it?.estimated_duration_minutes) || 0), 0);
    const minutesTotal = positive(cat?.estimated_duration_minutes) ?? positive(fromItems);
    // The assignment's own date first, then one an admin entered for the map.
    const dueDate = day(m.dueDate) ?? day(opts.adminDueDates?.[`journey_map:${m.mapId}`]);
    const expires = day(cat?.available_to);
    return {
      id: `jm-${m.mapId}`,
      source: 'journey',
      name: m.mapName,
      detail: '',
      // Assignments are Optional unless made Required; only a server that predates the flag leaves it unknown.
      required: m.isRequired ?? true,
      status: m.done ? 'completed' : percent > 0 || m.completedItems > 0 ? 'in_progress' : 'not_started',
      percent,
      progress: m.totalItems
        ? `${m.completedItems} of ${m.totalItems} steps${m.requiredPercent ? ` · pass at ${Math.round(m.requiredPercent)}%` : ''}`
        : '',
      minutesTotal,
      minutesLeft: minutesTotal == null ? null : m.done ? 0 : Math.round((minutesTotal * (100 - percent)) / 100),
      deadline: dueDate ?? expires,
      deadlineKind: dueDate ? 'due' : expires ? 'expires' : null,
      completedOn: day(m.completedDate),
      // Journey maps are taught on Cyber Academy; the map id is its training id.
      href: cyberAcademyTrainingUrl(m.mapId),
      external: true,
    };
  });
}

/** A series tagged with a fiscal year older than the current one is stale enablement. */
const isStaleFY = (name: string, now: Date): boolean => {
  const currentFY = (now.getFullYear() + (now.getMonth() >= 7 ? 1 : 0)) % 100;
  const tokens = Array.from((name || '').matchAll(/FY\s*'?(\d{2})/gi)).map((m) => Number(m[1]));
  return tokens.length > 0 && Math.max(...tokens) < currentFY;
};

/** Same rules as Stay on Track: certified counts as complete; closed or stale unfinished series are dropped. */
export function mindtickleRows(items: EducationItem[], opts: BuildOptions = {}): EnablementRow[] {
  const now = opts.now ?? new Date();
  return items
    .map((i) => (i.certified && i.status !== 'completed' ? { ...i, status: 'completed' as const } : i))
    .filter((i) => i.status === 'completed' || (!i.closed && !isStaleFY(i.series_name, now)))
    .map((i) => {
      const reqTotal = i.total_required_modules ?? 0;
      const total = reqTotal > 0 ? reqTotal : i.total_modules;
      const done = reqTotal > 0 ? i.completed_required_modules ?? 0 : i.completed_modules;
      // The API's own due date wins; a series without one falls back to when its quarterly or fiscal-year cadence ends.
      const cadence =
        i.window === 'current_quarter'
          ? opts.bounds?.current?.end ?? null
          : i.window === 'current_fiscal_year'
            ? opts.bounds?.yearEnd ?? null
            : null;
      const done_ = i.status === 'completed';
      // The API's own date wins; an admin's fills in when the source has none.
      const own = day(i.due_date);
      const admin = own ? null : day(opts.adminDueDates?.[`education_series:${i.series_id}`]);
      const expires = done_ ? day(i.expires_on) : null;
      const deadline = own ?? admin ?? expires ?? cadence;
      const kind = own || admin ? 'due' : expires ? 'expires' : deadline ? 'due' : null;
      return {
        id: `mt-${i.series_id}`,
        source: 'mindtickle',
        name: i.series_name,
        detail: [i.program_label, i.window ? WINDOWS[i.window] : null].filter(Boolean).join(' · '),
        required: !!i.required,
        status: i.status,
        percent: i.status === 'completed' ? 100 : clamp(i.percent_completed || 0),
        progress: total ? `${done} of ${total} ${reqTotal > 0 ? 'required ' : ''}modules` : '',
        minutesTotal: null,
        minutesLeft: null,
        deadline,
        deadlineKind: kind,
        completedOn: day(i.completed_on) ?? day(i.award_date),
        assignedOn: day(i.assigned_on),
        expiresOn: day(i.expires_on),
        dueSource: own ? i.due_date_source ?? null : null,
        href: i.series_id ? `${MINDTICKLE_URL}${i.series_id}` : null,
        external: true,
      } as EnablementRow;
    });
}

export const buildEnablementRows = (maps: UserJourneyMap[], items: EducationItem[], opts: BuildOptions = {}): EnablementRow[] => [
  ...journeyRows(maps, opts),
  ...mindtickleRows(items, opts),
];

/** Only what has to be completed: assigned journey maps and admin-required Mindtickle series. */
export const requiredRows = (rows: EnablementRow[]): EnablementRow[] => rows.filter((r) => r.required);

export function summarizeEnablement(rows: EnablementRow[]) {
  const by = (s: EnablementStatus) => rows.filter((r) => r.status === s).length;
  const source = (src: EnablementSource) => {
    const list = rows.filter((r) => r.source === src);
    return { done: list.filter((r) => r.status === 'completed').length, total: list.length };
  };
  const total = rows.length;
  const completed = by('completed');
  return {
    total,
    completed,
    inProgress: by('in_progress'),
    notStarted: by('not_started'),
    percent: total ? Math.round((completed / total) * 100) : 0,
    requiredOpen: rows.filter((r) => r.required && r.status !== 'completed').length,
    minutesLeft: rows.reduce((sum, r) => sum + (r.minutesLeft || 0), 0),
    journey: source('journey'),
    mindtickle: source('mindtickle'),
  };
}

export type DeadlineBucket = 'overdue' | 'current' | 'next' | 'later' | 'past' | 'none';

/** Where a deadline falls: already missed, this fiscal quarter, the next one, or beyond. */
export function deadlineBucket(row: EnablementRow, bounds: QuarterBounds | null | undefined, today: Date = new Date()): DeadlineBucket {
  if (!row.deadline) return 'none';
  const t = format(today, 'yyyy-MM-dd');
  if (row.deadline < t) return row.status === 'completed' ? 'past' : 'overdue';
  if (bounds?.current && row.deadline <= bounds.current.end) return 'current';
  if (bounds?.next && row.deadline <= bounds.next.end) return 'next';
  return 'later';
}

export interface EnablementFilters {
  statuses?: ReadonlySet<EnablementStatus>;
  query?: string;
  deadline?: DeadlineBucket | null;
  bounds?: QuarterBounds | null;
  today?: Date;
}

export function filterEnablement(rows: EnablementRow[], f: EnablementFilters): EnablementRow[] {
  const q = (f.query || '').trim().toLowerCase();
  return rows.filter(
    (r) =>
      (!f.statuses || !f.statuses.size || f.statuses.has(r.status)) &&
      (!f.deadline || deadlineBucket(r, f.bounds, f.today) === f.deadline) &&
      (!q || `${r.name} ${r.detail}`.toLowerCase().includes(q)),
  );
}

export type EnablementSortKey = 'name' | 'required' | 'status' | 'percent' | 'minutesLeft' | 'deadline' | 'completedOn';
// Action order: what you're partway through first, then what hasn't been started, then what's done.
const STATUS_ORDER: Record<EnablementStatus, number> = { in_progress: 0, not_started: 1, completed: 2 };

/** Sorts a copy; empty values always sink to the bottom whatever the direction. */
export function sortEnablement(rows: EnablementRow[], key: EnablementSortKey, dir: 'asc' | 'desc'): EnablementRow[] {
  const sign = dir === 'asc' ? 1 : -1;
  const val = (r: EnablementRow): string | number | null => {
    if (key === 'status') return STATUS_ORDER[r.status];
    if (key === 'required') return r.required ? 0 : 1;
    // A finished item has no time left to sort by.
    if (key === 'minutesLeft') return r.status === 'completed' ? null : r.minutesLeft;
    const v = r[key];
    return v === '' ? null : v;
  };
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const av = val(a.r);
      const bv = val(b.r);
      if (av == null || bv == null) return av == null && bv == null ? a.i - b.i : av == null ? 1 : -1;
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return cmp ? cmp * sign : a.i - b.i;
    })
    .map((x) => x.r);
}
